import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ListTodo,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  AlertCircle,
  FileSearch,
  Mail,
  Filter,
} from 'lucide-react'
import { jobsApi } from '@/services/api'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'

interface JobResultSummary {
  source?: string
  pages_scraped?: number
  companies_found?: number
  companies_saved?: number
  companies_processed?: number
  outreach_generated?: number
  qualified?: number
  rejected?: number
  emails_processed?: number
  emails_sent?: number
  errors?: number | string[]
}

interface Job {
  id: string
  name: string
  type: string
  status: string
  progress: number
  created_at: string
  completed_at: string | null
  error: string | null
  metadata?: { task_name?: string; payload?: Record<string, unknown> } | null
  result_summary?: JobResultSummary | null
}

function formatRelativeTime(date: string): string {
  const now = Date.now()
  const then = new Date(date).getTime()
  const diffSec = Math.floor((now - then) / 1000)
  if (diffSec < 60) return 'just now'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} hr ago`
  if (diffSec < 604800) return `${Math.floor(diffSec / 86400)} days ago`
  return new Date(date).toLocaleDateString()
}

function formatDuration(start: string, end: string | null): string | null {
  if (!end) return null
  const s = new Date(start).getTime()
  const e = new Date(end).getTime()
  const sec = Math.floor((e - s) / 1000)
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`
  return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`
}

function getFriendlyJobName(job: Job): string {
  const name = job.name || ''
  const payload = job.metadata?.payload || {}
  const source = payload.source || payload.source_name || job.result_summary?.source

  if (name.includes('scrape') || job.type === 'scraper') {
    return source ? `Scrape: ${source}` : 'Scrape Source'
  }
  if (name.includes('generate_outreach') || job.type === 'outreach') {
    if (name.includes('send')) return 'Send Outreach'
    return 'Generate Outreach'
  }
  if (name.includes('qualify') || job.type === 'qualification') {
    return source ? `Qualify: ${source}` : 'Qualify Companies'
  }
  return name || job.type || 'Background Job'
}

function getJobTypeIcon(type: string) {
  if (type === 'scraper') return <FileSearch className="h-4 w-4" />
  if (type === 'outreach') return <Mail className="h-4 w-4" />
  return <Filter className="h-4 w-4" />
}

function ResultSummary({ summary }: { summary: JobResultSummary }) {
  const parts: string[] = []
  if (summary.pages_scraped != null && summary.pages_scraped > 0) {
    parts.push(`${summary.pages_scraped} pages`)
  }
  if (summary.companies_found != null && summary.companies_found > 0) {
    parts.push(`${summary.companies_found} companies found`)
  }
  if (summary.companies_saved != null && summary.companies_saved > 0) {
    parts.push(`${summary.companies_saved} saved`)
  }
  if (summary.companies_processed != null && summary.companies_processed > 0) {
    parts.push(`${summary.companies_processed} processed`)
  }
  if (summary.outreach_generated != null && summary.outreach_generated > 0) {
    parts.push(`${summary.outreach_generated} outreach generated`)
  }
  if (summary.qualified != null && summary.qualified > 0) {
    parts.push(`${summary.qualified} qualified`)
  }
  if (summary.emails_sent != null && summary.emails_sent > 0) {
    parts.push(`${summary.emails_sent} emails sent`)
  }
  if (summary.errors != null) {
    const errCount = Array.isArray(summary.errors) ? summary.errors.length : summary.errors
    if (errCount > 0) parts.push(`${errCount} errors`)
  }
  if (parts.length === 0) return null
  return <span className="text-sm text-muted-foreground">{parts.join(' · ')}</span>
}

export default function JobsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data, isLoading, error, refetch } = useQuery<{ data: Job[]; page: number; limit: number }>({
    queryKey: ['jobs'],
    queryFn: () => jobsApi.list({ limit: 50 }),
    refetchInterval: 5000,
  })

  const jobs = Array.isArray(data?.data) ? data.data : []

  const filteredJobs = useMemo(() => {
    if (statusFilter === 'all') return jobs
    return jobs.filter((j) => j.status?.toLowerCase() === statusFilter)
  }, [jobs, statusFilter])

  const stats = useMemo(() => {
    const s = { total: jobs.length, running: 0, completed: 0, failed: 0, pending: 0 }
    jobs.forEach((j) => {
      const status = j.status?.toLowerCase()
      if (status === 'running') s.running++
      else if (status === 'completed') s.completed++
      else if (status === 'failed') s.failed++
      else s.pending++
    })
    return s
  }, [jobs])

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
      case 'failed':
        return <XCircle className="h-5 w-5 text-destructive shrink-0" />
      case 'running':
        return <Loader2 className="h-5 w-5 text-primary animate-spin shrink-0" />
      default:
        return <Clock className="h-5 w-5 text-muted-foreground shrink-0" />
    }
  }

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'default'
      case 'failed':
        return 'destructive'
      case 'running':
        return 'secondary'
      default:
        return 'outline'
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Jobs</h1>
          <p className="mt-1 text-muted-foreground">
            Background tasks and processing jobs for sourcing and outreach
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading jobs: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && jobs.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Running</p>
              <p className="text-2xl font-bold text-primary">{stats.running}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">Failed</p>
              <p className="text-2xl font-bold text-destructive">{stats.failed}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        {isLoading ? (
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto" />
            <p className="mt-2 text-muted-foreground">Loading jobs...</p>
          </CardContent>
        ) : jobs.length === 0 ? (
          <CardContent className="p-12 text-center">
            <ListTodo className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No jobs found</h3>
            <p className="text-muted-foreground">
              Jobs will appear here when you run scrape or qualification tasks
            </p>
          </CardContent>
        ) : (
          <>
            <div className="border-b px-4 pt-4">
              <Tabs value={statusFilter} onValueChange={setStatusFilter}>
                <TabsList>
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="running">Running</TabsTrigger>
                  <TabsTrigger value="completed">Completed</TabsTrigger>
                  <TabsTrigger value="failed">Failed</TabsTrigger>
                  <TabsTrigger value="pending">Pending</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            {filteredJobs.length === 0 && statusFilter !== 'all' ? (
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">No {statusFilter} jobs</p>
              </CardContent>
            ) : (
            <ul className="divide-y divide-border">
              {filteredJobs.map((job) => (
                <li key={job.id} className="p-4 hover:bg-muted/50 transition-colors">
                  <div className="flex items-start gap-4">
                    {getStatusIcon(job.status)}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-foreground">
                          {getFriendlyJobName(job)}
                        </p>
                        {(job.result_summary?.source ||
                          (job.metadata?.payload as Record<string, string> | undefined)?.source) && (
                          <Badge variant="outline" className="font-normal">
                            {getJobTypeIcon(job.type)}
                            <span className="ml-1">
                              {String(
                                job.result_summary?.source ||
                                  (job.metadata?.payload as Record<string, string> | undefined)?.source
                              )}
                            </span>
                          </Badge>
                        )}
                        <Badge variant={getStatusVariant(job.status)}>{job.status}</Badge>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
                        <span>Started {formatRelativeTime(job.created_at)}</span>
                        {job.completed_at && (
                          <span>
                            Duration:{' '}
                            {formatDuration(job.created_at, job.completed_at) ?? '—'}
                          </span>
                        )}
                      </div>
                      {job.result_summary && (
                        <div className="mt-2">
                          <ResultSummary summary={job.result_summary} />
                        </div>
                      )}
                      {job.error && (
                        <Alert variant="destructive" className="mt-2">
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>{job.error}</AlertDescription>
                        </Alert>
                      )}
                    </div>
                    {job.status === 'running' && (
                      <div className="w-28 shrink-0">
                        <Progress value={job.progress || 0} className="h-2" />
                        <p className="text-xs text-muted-foreground text-center mt-1">
                          {job.progress || 0}%
                        </p>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
